import React, { useRef, useState, useEffect, useCallback } from 'react'
import { Play, Pause, RotateCcw } from 'lucide-react'

import { prepCanvas } from './canvasUtil'

interface Props { title?: string; description?: string; onComplete?: () => void }

// Nhiệt dung riêng (Lớp 12 — Bài 4): Q = m·c·ΔT.
// Đun với công suất không đổi: nhiệt độ tăng tuyến tính theo thời gian, độ dốc dT/dt = P/(m·c).
// → Càng nhiều nước (m lớn) hoặc chất có c lớn → nóng càng CHẬM (độ dốc nhỏ).
const SUBSTANCES: { name: string; c: number }[] = [
  { name: 'Nước', c: 4200 }, { name: 'Nhôm', c: 880 }, { name: 'Sắt', c: 460 }, { name: 'Đồng', c: 380 },
]
const T0 = 20, TMAX = 100, M_MAX = 5
const HEAT_CONST = 42000 // P quy ước: m=1kg nước → ~10°C/s

const SpecificHeatSimulation: React.FC<Props> = ({
  title = 'Nhiệt dung riêng — Q = m·c·ΔT',
  description = 'Đun với công suất không đổi: nhiệt độ tăng theo thời gian. Càng nhiều nước hoặc chất có c lớn → nóng càng chậm (độ dốc đồ thị nhỏ).',
  onComplete,
}) => {
  const cv = useRef<HTMLCanvasElement>(null)
  const raf = useRef<number | null>(null)
  const lastRef = useRef<number | null>(null)
  const tRef = useRef(0)
  const TcurRef = useRef(T0)
  const histRef = useRef<{ t: number; T: number }[]>([{ t: 0, T: T0 }])
  const [running, setRunning] = useState(false)
  const [m, setM] = useState(1)
  const [si, setSi] = useState(0)
  const [Tdisp, setTdisp] = useState(T0)
  const firstRef = useRef(false)
  const c = SUBSTANCES[si].c
  const W = 620, H = 300

  const reset = useCallback(() => {
    setRunning(false); tRef.current = 0; TcurRef.current = T0; histRef.current = [{ t: 0, T: T0 }]; setTdisp(T0)
  }, [])
  useEffect(() => { reset() }, [m, si, reset])

  const draw = useCallback(() => {
    const ctx = prepCanvas(cv.current, W, H); if (!ctx) return
    ctx.clearRect(0, 0, W, H)
    const T = TcurRef.current
    const heat = (T - T0) / (TMAX - T0) // 0..1

    // ── Bình chứa (ĐỘ RỘNG ĐÁY CỐ ĐỊNH) ──
    const bx = 40, bw = 90, bTop = 40, bBottom = H - 36, innerH = bBottom - bTop
    const waterH = (m / M_MAX) * innerH
    const waterTop = bBottom - waterH
    // nước (màu theo nhiệt độ: xanh → đỏ)
    const r = Math.round(60 + heat * 195), g = Math.round(130 - heat * 90), b = Math.round(246 - heat * 200)
    const grd = ctx.createLinearGradient(0, waterTop, 0, bBottom)
    grd.addColorStop(0, `rgb(${r},${g + 40},${b})`); grd.addColorStop(1, `rgb(${r},${g},${b})`)
    ctx.fillStyle = grd; ctx.fillRect(bx, waterTop, bw, waterH)
    // thành bình
    ctx.strokeStyle = '#475569'; ctx.lineWidth = 3
    ctx.beginPath(); ctx.moveTo(bx, bTop); ctx.lineTo(bx, bBottom); ctx.lineTo(bx + bw, bBottom); ctx.lineTo(bx + bw, bTop); ctx.stroke()
    // vạch mức nước
    ctx.strokeStyle = 'rgba(148,163,184,0.7)'; ctx.lineWidth = 1; ctx.setLineDash([4, 3])
    ctx.beginPath(); ctx.moveTo(bx, waterTop); ctx.lineTo(bx + bw, waterTop); ctx.stroke(); ctx.setLineDash([])
    ctx.fillStyle = '#334155'; ctx.font = 'bold 12px sans-serif'; ctx.fillText(`${m} kg`, bx + bw / 2 - 14, waterTop + 16)
    // ngọn lửa
    ctx.font = '20px sans-serif'; for (let i = 0; i < 3; i++) ctx.fillText('🔥', bx + 14 + i * 22, bBottom + 24)

    // ── Đồ thị T(t) ──
    const gx0 = 200, gy0 = H - 36, gw = W - gx0 - 16, gh = H - 60
    const TT = 24 // trục thời gian (s)
    ctx.strokeStyle = '#eef2f7'; ctx.lineWidth = 1
    for (let i = 0; i <= 8; i++) { const gx = gx0 + (i / 8) * gw; ctx.beginPath(); ctx.moveTo(gx, 16); ctx.lineTo(gx, gy0); ctx.stroke() }
    // trục ĐẬM, SẮC NÉT + mũi tên
    ctx.strokeStyle = '#1e293b'; ctx.fillStyle = '#1e293b'; ctx.lineWidth = 2.5; ctx.lineJoin = 'round'
    ctx.beginPath(); ctx.moveTo(gx0, 14); ctx.lineTo(gx0, gy0); ctx.lineTo(gx0 + gw, gy0); ctx.stroke()
    // mũi tên trục đứng (lên) & trục ngang (phải)
    ctx.beginPath(); ctx.moveTo(gx0, 7); ctx.lineTo(gx0 - 5, 17); ctx.lineTo(gx0 + 5, 17); ctx.closePath(); ctx.fill()
    ctx.beginPath(); ctx.moveTo(gx0 + gw + 7, gy0); ctx.lineTo(gx0 + gw - 4, gy0 - 5); ctx.lineTo(gx0 + gw - 4, gy0 + 5); ctx.closePath(); ctx.fill()
    // nhãn trục
    ctx.font = 'bold 13px sans-serif'; ctx.textAlign = 'right'
    ctx.fillText('Thời gian (s)', gx0 + gw + 2, gy0 + 18); ctx.textAlign = 'left'
    ctx.save(); ctx.translate(gx0 - 16, (14 + gy0) / 2); ctx.rotate(-Math.PI / 2); ctx.textAlign = 'center'; ctx.fillText('Nhiệt độ (°C)', 0, 0); ctx.restore(); ctx.textAlign = 'left'
    const X = (t: number) => gx0 + Math.min(t / TT, 1) * gw
    const Y = (temp: number) => gy0 - ((temp - T0) / (TMAX - T0)) * gh
    // đường T(t) (đậm)
    ctx.strokeStyle = '#dc2626'; ctx.lineWidth = 3; ctx.beginPath()
    histRef.current.forEach((p, i) => { i === 0 ? ctx.moveTo(X(p.t), Y(p.T)) : ctx.lineTo(X(p.t), Y(p.T)) })
    ctx.stroke()
    const last = histRef.current[histRef.current.length - 1]
    ctx.fillStyle = '#dc2626'; ctx.beginPath(); ctx.arc(X(last.t), Y(last.T), 5, 0, Math.PI * 2); ctx.fill()
  }, [m, si])

  const tick = useCallback((ts: number) => {
    if (lastRef.current == null) lastRef.current = ts
    const dt = Math.min(0.05, (ts - lastRef.current) / 1000); lastRef.current = ts
    const rate = HEAT_CONST / (m * c) // °C/s
    let T = TcurRef.current + rate * dt
    if (T >= TMAX) { T = TMAX }
    TcurRef.current = T; tRef.current += dt
    const h = histRef.current; h.push({ t: tRef.current, T }); if (h.length > 1500) h.shift()
    setTdisp(T)
    draw()
    if (T >= TMAX || tRef.current >= 24) { setRunning(false); return }
    raf.current = requestAnimationFrame(tick)
  }, [m, c, draw])

  useEffect(() => {
    if (running) { if (!firstRef.current) { firstRef.current = true; onComplete?.() } lastRef.current = null; raf.current = requestAnimationFrame(tick) }
    else if (raf.current) cancelAnimationFrame(raf.current)
    return () => { if (raf.current) cancelAnimationFrame(raf.current) }
  }, [running, tick, onComplete])
  useEffect(() => { draw() }, [draw])

  const dT = Math.round(Tdisp - T0)
  const Q = m * c * dT
  return (
    <div className="rounded-2xl border border-amber-200 bg-white p-4 my-3">
      <h4 className="font-bold text-amber-800">{title}</h4>
      <p className="text-sm text-gray-500 mb-3">{description}</p>
      <canvas ref={cv} width={W} height={H} className="w-full rounded-lg border border-gray-100 bg-slate-50" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 my-3 text-center">
        <div className="p-2 rounded-lg bg-rose-50"><div className="font-black text-rose-700">{Math.round(Tdisp)}°C</div><div className="text-[11px] text-gray-500">Nhiệt độ T</div></div>
        <div className="p-2 rounded-lg bg-blue-50"><div className="font-black text-blue-700">{dT}°C</div><div className="text-[11px] text-gray-500">Độ tăng ΔT</div></div>
        <div className="p-2 rounded-lg bg-amber-50"><div className="font-black text-amber-700">{(Q / 1000).toFixed(1)} kJ</div><div className="text-[11px] text-gray-500">Q=m·c·ΔT</div></div>
        <div className="p-2 rounded-lg bg-emerald-50"><div className="font-black text-emerald-700">{(HEAT_CONST / (m * c)).toFixed(1)}</div><div className="text-[11px] text-gray-500">Độ dốc °C/s</div></div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
        <label className="flex items-center gap-2"><span className="w-32 text-gray-600">Lượng nước (cột)</span><input type="range" min={0.5} max={5} step={0.5} value={m} onChange={(e) => setM(Number(e.target.value))} className="flex-1" /><span className="w-12 text-right font-semibold">{m} kg</span></label>
        <label className="flex items-center gap-2"><span className="w-16 text-gray-600">Chất</span>
          <select value={si} onChange={(e) => setSi(Number(e.target.value))} className="flex-1 px-2 py-1.5 rounded-lg border border-gray-200 text-sm">
            {SUBSTANCES.map((s, i) => <option key={i} value={i}>{s.name} (c={s.c})</option>)}
          </select></label>
      </div>
      <div className="flex gap-2 mt-3">
        <button onClick={() => { if (TcurRef.current >= TMAX) reset(); setRunning((r) => !r) }} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-500 text-white font-bold hover:bg-amber-600">
          {running ? <><Pause className="w-4 h-4" /> Tạm dừng</> : <><Play className="w-4 h-4" /> Đun</>}</button>
        <button onClick={reset} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-200 text-gray-600 font-bold hover:bg-gray-50"><RotateCcw className="w-4 h-4" /> Đặt lại</button>
      </div>
    </div>
  )
}
export default SpecificHeatSimulation
